import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilePdfOutlined,
  MailOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import customerService from '@/services/customer.service';
import { formatCustomerOptionLabel } from '@/utils/customerOptionLabel';
import {
  getCustomerStatement,
  getCustomerStatementDispatches,
  getCustomerStatementPdf,
  type CustomerStatement,
  type CustomerStatementDispatch,
  type CustomerStatementEntityType,
  type CustomerStatementMode,
  type CustomerStatementOpenItem,
  type CustomerStatementParams,
  type CustomerStatementSection,
  type CustomerStatementTransaction,
} from '@/services/financialReports.service';
import { formatMoney, formatNumber } from '@/utils/format';
import { useAuthStore } from '@/store/authStore';
import StatementAgeingBar from '@/components/finance/customer-statement/StatementAgeingBar';
import SendStatementModal from '@/components/finance/customer-statement/SendStatementModal';
import { PERIOD_PRESETS, presetRange, type PeriodPresetKey } from '@/components/finance/report-document/periodPresets';
import { openBlobInNewTab, triggerDownload } from '@/reports/utils/exportTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ENTITY_PATH: Record<CustomerStatementEntityType, (id: string) => string> = {
  sales_invoice: (id) => `/sales/invoices/${id}`,
  receipt_voucher: (id) => `/fin/receipt-vouchers/${id}`,
  customer_credit_note: (id) => `/fin/credit-notes/${id}`,
};

const PRESET_I18N: Record<PeriodPresetKey, string> = {
  this_month: 'thisMonth',
  last_month: 'lastMonth',
  this_quarter: 'thisQuarter',
  last_quarter: 'lastQuarter',
  ytd: 'ytd',
  last_year: 'lastYear',
  last_12: 'last12Months',
  custom: 'custom',
};

const fmtDate = (iso: string | null | undefined) => (iso ? dayjs(iso).format('DD-MM-YYYY') : '—');
const amount = (n: number) => formatMoney(Math.abs(n) < 0.005 ? 0 : n, { display: 'plain', locale: 'en-US' });
const money = (n: number, ccy: string) => `${ccy} ${amount(n)}`;

const csvAmount = (n: number) =>
  formatNumber(Math.abs(n) < 0.005 ? 0 : n, { locale: 'en-US', minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });

function csvCell(v: unknown): string {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}

function openItemKindLabel(kind: CustomerStatementOpenItem['kind'], t: TFunction): string {
  if (kind === 'invoice') return t('invoice');
  if (kind === 'unapplied_receipt') return t('unappliedPayment');
  return t('unappliedCreditNote');
}

function exportStatementCsv(s: CustomerStatement, t: TFunction): void {
  const lines: string[] = [];
  lines.push([t('statementOfAccount'), s.statement_ref].map(csvCell).join(','));
  lines.push([t('customer'), `${s.customer.customer_name} (${s.customer.customer_code})`].map(csvCell).join(','));
  lines.push([t('period'), t('periodRange', { from: fmtDate(s.period.from), to: fmtDate(s.period.to) })].map(csvCell).join(','));
  for (const sec of s.sections) {
    lines.push('');
    lines.push([t('accountActivityCurrency', { currency: sec.currency })].map(csvCell).join(','));
    lines.push(
      [
        t('date'),
        t('type'),
        t('documentNo'),
        t('customerLpo'),
        t('deliveryNotes'),
        t('customerOrder'),
        t('dueDate'),
        t('detailsCol'),
        t('debit'),
        t('credit'),
        t('balance'),
      ]
        .map(csvCell)
        .join(',')
    );
    lines.push(
      [fmtDate(s.period.from), t('openingBalance'), '', '', '', '', '', '', '', '', csvAmount(sec.opening_balance)].map(csvCell).join(',')
    );
    for (const tx of sec.transactions) {
      lines.push(
        [
          fmtDate(tx.date),
          tx.type_label,
          tx.document_number,
          tx.customer_lpo ?? '',
          tx.delivery_notes.join(' / '),
          tx.sales_order_number ?? '',
          tx.type === 'invoice' ? fmtDate(tx.due_date) : '',
          tx.description,
          tx.debit ? csvAmount(tx.debit) : '',
          tx.credit ? csvAmount(tx.credit) : '',
          csvAmount(tx.balance),
        ]
          .map(csvCell)
          .join(',')
      );
    }
    lines.push(['', t('closingBalance'), '', '', '', '', '', '', '', '', csvAmount(sec.closing_balance)].map(csvCell).join(','));
    lines.push('');
    lines.push([t('openItemsAsAt', { currency: sec.currency, date: fmtDate(s.period.to) })].map(csvCell).join(','));
    lines.push(
      [
        t('documentNo'),
        t('kind'),
        t('date'),
        t('customerLpo'),
        t('deliveryNotes'),
        t('customerOrder'),
        t('dueDate'),
        t('daysOverdueCol'),
        t('amount'),
        t('paidAdjusted'),
        t('balance'),
      ]
        .map(csvCell)
        .join(',')
    );
    for (const o of sec.open_items) {
      lines.push(
        [
          o.document_number,
          openItemKindLabel(o.kind, t),
          fmtDate(o.date),
          o.customer_lpo ?? '',
          o.delivery_notes.join(' / '),
          o.sales_order_number ?? '',
          o.kind === 'invoice' ? fmtDate(o.due_date ?? o.date) : '',
          o.kind === 'invoice' ? o.days_overdue : '',
          csvAmount(o.original_amount),
          csvAmount(o.settled_amount),
          csvAmount(o.open_amount),
        ]
          .map(csvCell)
          .join(',')
      );
    }
  }
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${s.statement_ref}.csv`);
}

const DocLink: React.FC<{ type: CustomerStatementEntityType; id: string; label: string }> = ({ type, id, label }) => {
  const navigate = useNavigate();
  if (!id) return <span>{label}</span>;
  const href = ENTITY_PATH[type](id);
  return (
    <a
      href={href}
      onClick={(e) => {
        e.preventDefault();
        navigate(href);
      }}
    >
      {label}
    </a>
  );
};

const DnList: React.FC<{ numbers: string[] }> = ({ numbers }) => {
  if (!numbers.length) return <Text type="secondary">—</Text>;
  if (numbers.length <= 2) return <span>{numbers.join(', ')}</span>;
  return (
    <Tooltip title={numbers.join(', ')}>
      <span>
        {numbers.slice(0, 2).join(', ')} <Tag style={{ marginLeft: 4 }}>+{numbers.length - 2}</Tag>
      </span>
    </Tooltip>
  );
};

export const CustomerStatementPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canSend = Boolean(user?.is_super_admin || user?.permissions?.includes('finance.customer_statement.send'));

  const urlFrom = searchParams.get('from');
  const urlTo = searchParams.get('to');
  const [customerId, setCustomerId] = useState<string | undefined>(searchParams.get('customer_id') ?? undefined);
  const [preset, setPreset] = useState<PeriodPresetKey>(urlFrom || urlTo ? 'custom' : 'ytd');
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() =>
    urlFrom && urlTo ? [dayjs(urlFrom), dayjs(urlTo)] : (presetRange('ytd') as [Dayjs, Dayjs])
  );
  const [mode, setMode] = useState<CustomerStatementMode>(
    searchParams.get('mode') === 'open_items' ? 'open_items' : 'activity'
  );
  const [currency, setCurrency] = useState<string | undefined>(searchParams.get('currency') ?? undefined);
  const [params, setParams] = useState<CustomerStatementParams | null>(null);
  const [activeTab, setActiveTab] = useState<string>('activity');
  const [emailOpen, setEmailOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState<'preview' | 'download' | null>(null);

  const periodPresetOptions = useMemo(
    () =>
      PERIOD_PRESETS.map((p) => ({
        value: p.value,
        label: tc(PRESET_I18N[p.value]),
      })),
    [tc]
  );

  const { data: customersList = [] } = useQuery({
    queryKey: ['statement-customers'],
    queryFn: () => customerService.getActiveCustomers(),
  });
  const customerOptions = useMemo(
    () =>
      (customersList as Array<{
        id: string;
        customer_name?: string;
        customer_code?: string;
        student_name?: string | null;
        student_admission_no?: string | null;
        contact_person?: string | null;
      }>).map((c) => ({
        value: c.id,
        label: formatCustomerOptionLabel(c),
      })),
    [customersList]
  );

  const buildParams = useCallback(
    (): CustomerStatementParams | null =>
      customerId
        ? {
            customer_id: customerId,
            from_date: range[0].format('YYYY-MM-DD'),
            to_date: range[1].format('YYYY-MM-DD'),
            currency,
            mode,
          }
        : null,
    [customerId, range, currency, mode]
  );

  const generate = useCallback(() => {
    const p = buildParams();
    if (!p) return;
    setParams(p);
    const next: Record<string, string> = { customer_id: p.customer_id, from: p.from_date, to: p.to_date };
    if (p.mode === 'open_items') next.mode = 'open_items';
    if (p.currency) next.currency = p.currency;
    setSearchParams(next, { replace: true });
  }, [buildParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('customer_id') && !params) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statementQuery = useQuery({
    queryKey: ['customer-statement', params],
    queryFn: () => getCustomerStatement(params as CustomerStatementParams),
    enabled: !!params,
  });
  const statement = statementQuery.data;

  const dispatchesQuery = useQuery({
    queryKey: ['customer-statement-dispatches', params?.customer_id],
    queryFn: () => getCustomerStatementDispatches(params!.customer_id),
    enabled: !!params?.customer_id,
  });

  const [knownCurrencies, setKnownCurrencies] = useState<string[]>([]);
  useEffect(() => {
    if (!statement) return;
    setKnownCurrencies((prev) => [...new Set([...prev, ...statement.sections.map((s) => s.currency)])].sort());
  }, [statement]);

  const fetchPdf = async (kind: 'preview' | 'download') => {
    if (!params) return;
    setPdfBusy(kind);
    try {
      const blob = await getCustomerStatementPdf({ ...params, mode });
      if (kind === 'preview') {
        openBlobInNewTab(blob);
      } else {
        triggerDownload(blob, `${statement?.statement_ref ?? t('statement')}.pdf`);
      }
    } catch (err: any) {
      message.error(err?.message || tc('couldNotGenerateFile'));
    } finally {
      setPdfBusy(null);
    }
  };

  const base = statement?.base_currency ?? 'AED';
  const integrityIssues = statement?.sections.filter((s) => Math.abs(s.integrity_difference) >= 0.01) ?? [];

  const transactionColumns = (sec: CustomerStatementSection): ColumnsType<CustomerStatementTransaction & { key: string }> => [
    { title: t('date'), dataIndex: 'date', width: 100, render: fmtDate },
    {
      title: t('type'),
      dataIndex: 'type_label',
      width: 150,
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={r.type === 'invoice' ? 'blue' : r.type === 'receipt' ? 'green' : r.type === 'credit_note' ? 'purple' : 'gold'}>{v}</Tag>
          {r.type !== 'invoice' && r.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.description}
            </Text>
          ) : null}
        </Space>
      ),
    },
    { title: t('documentNo'), dataIndex: 'document_number', width: 150, render: (v: string, r) => <DocLink type={r.entity_type} id={r.document_id} label={v} /> },
    {
      title: t('customerLpo'),
      dataIndex: 'customer_lpo',
      width: 130,
      render: (v: string | null, r) =>
        v ? (
          <Tooltip title={r.customer_lpo_date ? t('lpoDate', { date: fmtDate(r.customer_lpo_date) }) : undefined}>
            <Text strong>{v}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: t('deliveryNotes'), dataIndex: 'delivery_notes', width: 190, render: (v: string[]) => <DnList numbers={v} /> },
    { title: t('customerOrder'), dataIndex: 'sales_order_number', width: 140, render: (v: string | null) => v ?? <Text type="secondary">—</Text> },
    { title: t('dueDate'), dataIndex: 'due_date', width: 100, render: (v: string | null, r) => (r.type === 'invoice' ? fmtDate(v) : '') },
    { title: t('debit'), dataIndex: 'debit', width: 120, align: 'right', render: (v: number) => (v ? amount(v) : '') },
    {
      title: t('credit'),
      dataIndex: 'credit',
      width: 120,
      align: 'right',
      render: (v: number) => (v ? <Text style={{ color: '#1E7B4F' }}>{amount(v)}</Text> : ''),
    },
    { title: t('balanceWithCurrency', { currency: sec.currency }), dataIndex: 'balance', width: 140, align: 'right', render: (v: number) => <Text strong>{amount(v)}</Text> },
  ];

  const openItemColumns = (sec: CustomerStatementSection): ColumnsType<CustomerStatementOpenItem & { key: string }> => [
    {
      title: t('documentNo'),
      dataIndex: 'document_number',
      width: 170,
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <DocLink type={r.entity_type} id={r.document_id} label={v} />
          {r.kind !== 'invoice' && (
            <Tag color="green">{openItemKindLabel(r.kind, t)}</Tag>
          )}
        </Space>
      ),
    },
    { title: t('date'), dataIndex: 'date', width: 100, render: fmtDate },
    { title: t('customerLpo'), dataIndex: 'customer_lpo', width: 130, render: (v: string | null) => (v ? <Text strong>{v}</Text> : <Text type="secondary">—</Text>) },
    { title: t('deliveryNotes'), dataIndex: 'delivery_notes', width: 190, render: (v: string[]) => <DnList numbers={v} /> },
    { title: t('dueDate'), dataIndex: 'due_date', width: 100, render: (v: string | null, r) => (r.kind === 'invoice' ? fmtDate(v ?? r.date) : '') },
    {
      title: t('overdue'),
      dataIndex: 'days_overdue',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.days_overdue - b.days_overdue,
      render: (v: number, r) =>
        r.kind !== 'invoice' ? '' : v > 0 ? <Tag color={v > 90 ? 'red' : v > 30 ? 'volcano' : 'orange'}>{t('daysCount', { count: v })}</Tag> : <Tag color="green">{t('current')}</Tag>,
    },
    { title: t('amount'), dataIndex: 'original_amount', width: 120, align: 'right', render: (v: number, r) => (r.kind === 'invoice' ? amount(v) : '') },
    { title: t('paidAdjusted'), dataIndex: 'settled_amount', width: 130, align: 'right', render: (v: number, r) => (r.kind === 'invoice' ? amount(v) : '') },
    {
      title: t('balanceWithCurrency', { currency: sec.currency }),
      dataIndex: 'open_amount',
      width: 140,
      align: 'right',
      render: (v: number) => <Text strong style={{ color: v < 0 ? '#1E7B4F' : undefined }}>{amount(v)}</Text>,
    },
  ];

  const dispatchColumns: ColumnsType<CustomerStatementDispatch> = [
    { title: t('sent'), dataIndex: 'sent_at', width: 160, render: (v: string) => dayjs(v).format('DD-MM-YYYY HH:mm') },
    { title: t('statement'), dataIndex: 'statement_ref', width: 200 },
    { title: t('period'), key: 'period', width: 200, render: (_: unknown, r) => t('periodRange', { from: fmtDate(r.period_from), to: fmtDate(r.period_to) }) },
    { title: t('type'), dataIndex: 'mode', width: 110, render: (v: string) => (v === 'open_items' ? t('openItems') : t('activity')) },
    {
      title: t('recipients'),
      dataIndex: 'recipients',
      render: (v: string[], r) => [...v, ...r.cc.map((c) => t('ccRecipient', { email: c }))].join(', '),
    },
    { title: t('balance'), dataIndex: 'closing_balance', width: 150, align: 'right', render: (v: number, r) => money(v, r.currency || base) },
    { title: t('by'), dataIndex: 'sent_by_name', width: 140, render: (v: string | null) => v ?? '—' },
    {
      title: t('status'),
      dataIndex: 'status',
      width: 100,
      render: (v: string, r) => (
        <Tooltip title={r.error_message ?? undefined}>
          <Tag color={v === 'Sent' ? 'green' : 'red'}>{v === 'Sent' ? t('sent') : v}</Tag>
        </Tooltip>
      ),
    },
  ];

  const renderSection = (sec: CustomerStatementSection) => {
    const fmt = (n: number) => money(n, sec.currency);
    return (
      <Card
        key={sec.currency}
        style={{ marginTop: 16 }}
        title={statement && statement.sections.length > 1 ? t('currencyAccount', { currency: sec.currency }) : t('accountSummary')}
        extra={<Text type="secondary">{t('allAmountsIn', { currency: sec.currency })}</Text>}
      >
        <Row gutter={[16, 16]}>
          {mode === 'activity' && (
            <>
              <Col xs={12} md={8} xl={4}>
                <Statistic title={t('openingWithDate', { date: fmtDate(statement?.period.from) })} value={amount(sec.opening_balance)} />
              </Col>
              <Col xs={12} md={8} xl={4}>
                <Statistic title={t('invoiced')} value={amount(sec.invoiced)} />
              </Col>
              <Col xs={12} md={8} xl={4}>
                <Statistic title={t('received')} value={amount(sec.received)} valueStyle={{ color: '#1E7B4F' }} />
              </Col>
              <Col xs={12} md={8} xl={4}>
                <Statistic title={t('creditsAndAdjustments')} value={amount(sec.credit_notes + sec.adjustments)} />
              </Col>
            </>
          )}
          {mode === 'open_items' && (
            <>
              <Col xs={12} md={8} xl={4}>
                <Statistic title={t('notYetDue')} value={amount(sec.not_due)} />
              </Col>
              <Col xs={12} md={8} xl={4}>
                <Statistic title={t('unappliedCredits')} value={amount(sec.unapplied_credits)} valueStyle={{ color: '#1E7B4F' }} />
              </Col>
            </>
          )}
          <Col xs={12} md={8} xl={4}>
            <Statistic title={t('overdue')} value={amount(sec.overdue)} valueStyle={{ color: sec.overdue > 0 ? '#B42318' : undefined }} />
          </Col>
          <Col xs={12} md={8} xl={4}>
            <Card size="small" style={{ background: '#243B53', borderColor: '#243B53' }} styles={{ body: { padding: 12 } }}>
              <Text style={{ color: '#C9D6E3', fontSize: 12 }}>{t('balanceDueWithDate', { date: fmtDate(statement?.period.to) })}</Text>
              <div style={{ color: '#fff', fontSize: 22, fontWeight: 600 }}>{fmt(sec.closing_balance)}</div>
              {!sec.is_base_currency && (
                <Text style={{ color: '#C9D6E3', fontSize: 12 }}>≈ {money(sec.closing_balance_base, base)}</Text>
              )}
            </Card>
          </Col>
        </Row>
        <div style={{ marginTop: 20 }}>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('ageingOfBalanceByDueDate')}
          </Text>
          <div style={{ marginTop: 8 }}>
            <StatementAgeingBar ageing={sec.ageing} format={fmt} />
          </div>
        </div>
      </Card>
    );
  };

  const tabItems = statement
    ? [
        ...(mode === 'activity'
          ? [
              {
                key: 'activity',
                label: t('accountActivityCount', { count: statement.sections.reduce((n, s) => n + s.transactions.length, 0) }),
                children: statement.sections.map((sec) => (
                  <div key={sec.currency} style={{ marginBottom: 16 }}>
                    {statement.sections.length > 1 && <Title level={5}>{sec.currency}</Title>}
                    <Table
                      size="small"
                      columns={transactionColumns(sec)}
                      dataSource={sec.transactions.map((tx, i) => ({ ...tx, key: `${tx.document_id}-${tx.type}-${i}` }))}
                      pagination={sec.transactions.length > 50 ? { defaultPageSize: 50, showSizeChanger: true } : false}
                      scroll={{ x: 1400 }}
                      title={() => (
                        <Space>
                          <Text type="secondary">{t('openingBalanceDate', { date: fmtDate(statement.period.from) })}</Text>
                          <Text strong>{money(sec.opening_balance, sec.currency)}</Text>
                        </Space>
                      )}
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={7}>
                            <Text strong>{t('closingBalanceDate', { date: fmtDate(statement.period.to) })}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            <Text strong>{amount(sec.invoiced)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={8} align="right">
                            <Text strong>{amount(sec.received + sec.adjustments + sec.credit_notes)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={9} align="right">
                            <Text strong>{amount(sec.closing_balance)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </div>
                )),
              },
            ]
          : []),
        {
          key: 'open_items',
          label: t('openItemsCount', { count: statement.sections.reduce((n, s) => n + s.open_items.length, 0) }),
          children: statement.sections.map((sec) => (
            <div key={sec.currency} style={{ marginBottom: 16 }}>
              {statement.sections.length > 1 && <Title level={5}>{sec.currency}</Title>}
              <Table
                size="small"
                columns={openItemColumns(sec)}
                dataSource={sec.open_items.map((o) => ({ ...o, key: `${o.kind}-${o.document_id}` }))}
                pagination={sec.open_items.length > 50 ? { defaultPageSize: 50, showSizeChanger: true } : false}
                scroll={{ x: 1200 }}
                locale={{ emptyText: <Empty description={t('noOpenItemsFullySettled')} /> }}
                rowClassName={(r) => (r.days_overdue > 60 ? 'statement-row-overdue' : '')}
                summary={() =>
                  sec.open_items.length ? (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={8}>
                        <Text strong>{t('totalBalanceDue')}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <Text strong>{amount(sec.closing_balance)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  ) : null
                }
              />
            </div>
          )),
        },
        {
          key: 'history',
          label: t('sendHistoryCount', { count: dispatchesQuery.data?.length ?? 0 }),
          children: (
            <Table
              size="small"
              rowKey="id"
              loading={dispatchesQuery.isLoading}
              columns={dispatchColumns}
              dataSource={dispatchesQuery.data ?? []}
              pagination={{ defaultPageSize: 20 }}
              scroll={{ x: 1200 }}
              locale={{ emptyText: <Empty description={t('noStatementEmailedYet')} /> }}
            />
          ),
        },
      ]
    : [];

  useEffect(() => {
    if (mode === 'open_items' && activeTab === 'activity') setActiveTab('open_items');
  }, [mode, activeTab]);

  return (
    <div className="customer-statement-page">
      <Space align="baseline" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            {t('customerStatementOfAccount')}
          </Title>
          <Text type="secondary">{t('customerStatementHint')}</Text>
        </div>
      </Space>

      <Card style={{ marginTop: 16 }}>
        <Space wrap size="middle" align="end">
          <Space direction="vertical" size={4}>
            <Text type="secondary">{t('customer')}</Text>
            <Select
              placeholder={t('selectCustomer')}
              value={customerId}
              onChange={setCustomerId}
              options={customerOptions}
              style={{ width: 320 }}
              showSearch
              optionFilterProp="label"
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Text type="secondary">{t('period')}</Text>
            <Select
              value={preset}
              style={{ width: 150 }}
              options={periodPresetOptions}
              onChange={(v: PeriodPresetKey) => {
                setPreset(v);
                const r = presetRange(v);
                if (r) setRange(r);
              }}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Text type="secondary">{t('fromToStatementDate')}</Text>
            <RangePicker
              value={range}
              format="DD-MM-YYYY"
              allowClear={false}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) {
                  setRange([dates[0], dates[1]]);
                  setPreset('custom');
                }
              }}
            />
          </Space>
          <Space direction="vertical" size={4}>
            <Text type="secondary">{t('statementType')}</Text>
            <Segmented
              value={mode}
              onChange={(v) => setMode(v as CustomerStatementMode)}
              options={[
                { value: 'activity', label: t('activityPlusOpenItems') },
                { value: 'open_items', label: t('openItemsOnly') },
              ]}
            />
          </Space>
          {knownCurrencies.length > 1 && (
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('currency')}</Text>
              <Select
                value={currency ?? ''}
                style={{ width: 130 }}
                onChange={(v: string) => setCurrency(v || undefined)}
                options={[{ value: '', label: t('all') }, ...knownCurrencies.map((c) => ({ value: c, label: c }))]}
              />
            </Space>
          )}
          <Button type="primary" icon={<SearchOutlined />} onClick={generate} disabled={!customerId}>
            {tc('generate')}
          </Button>
        </Space>
      </Card>

      {statementQuery.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message={t('couldNotLoadStatement')}
          description={(statementQuery.error as any)?.response?.data?.message || (statementQuery.error as Error)?.message}
        />
      )}

      {statementQuery.isFetching && !statement && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin tip={t('buildingStatement')} />
        </div>
      )}

      {!params && (
        <Card style={{ marginTop: 16 }}>
          <Empty description={t('selectCustomerPeriodGenerate')} />
        </Card>
      )}

      {statement && (
        <Spin spinning={statementQuery.isFetching}>
          <Card style={{ marginTop: 16 }}>
            <Row gutter={[24, 16]} align="top">
              <Col xs={24} lg={14}>
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
                    {t('statementTo')}
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {statement.customer.customer_name}
                  </Title>
                  {statement.customer.address_lines.map((l) => (
                    <Text key={l}>{l}</Text>
                  ))}
                </Space>
                <Descriptions size="small" column={{ xs: 1, md: 2 }} style={{ marginTop: 12 }}>
                  <Descriptions.Item label={t('customerCode')}>{statement.customer.customer_code || '—'}</Descriptions.Item>
                  <Descriptions.Item label={t('trn')}>{statement.customer.tax_registration_number || '—'}</Descriptions.Item>
                  <Descriptions.Item label={t('contact')}>{statement.customer.contact_person || '—'}</Descriptions.Item>
                  <Descriptions.Item label={t('email')}>
                    {statement.customer.email || <Text type="warning">{t('notSetOnCustomerMaster')}</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('paymentTerms')}>
                    {statement.summary.payment_terms ||
                      (statement.summary.credit_days != null ? t('daysCount', { count: statement.summary.credit_days }) : '—')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('creditLimit')}>
                    {statement.summary.credit_limit != null ? (
                      <span>
                        {money(statement.summary.credit_limit, base)}{' '}
                        <Text type={statement.summary.credit_available != null && statement.summary.credit_available < 0 ? 'danger' : 'secondary'}>
                          {t('availableAmount', { amount: money(statement.summary.credit_available ?? 0, base) })}
                        </Text>
                      </span>
                    ) : (
                      '—'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('lastPayment')}>
                    {statement.summary.last_payment
                      ? t('amountOnDate', {
                          amount: money(statement.summary.last_payment.amount, statement.summary.last_payment.currency),
                          date: fmtDate(statement.summary.last_payment.date),
                        })
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('oldestOverdue')}>
                    {statement.summary.oldest_overdue_days > 0 ? (
                      <Text type="danger">{t('daysCount', { count: statement.summary.oldest_overdue_days })}</Text>
                    ) : (
                      t('none')
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col xs={24} lg={10}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label={t('statementNo')}>{statement.statement_ref}</Descriptions.Item>
                    <Descriptions.Item label={t('period')}>
                      {t('periodRange', { from: fmtDate(statement.period.from), to: fmtDate(statement.period.to) })}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('totalBalanceDue')}>
                      <Text strong style={{ fontSize: 16 }}>
                        {money(statement.summary.closing_balance, base)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('overdue')}>
                      <Text strong type={statement.summary.overdue > 0 ? 'danger' : undefined}>
                        {money(statement.summary.overdue, base)}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                  <Space wrap>
                    <Button icon={<EyeOutlined />} loading={pdfBusy === 'preview'} onClick={() => fetchPdf('preview')}>
                      {tc('previewPdf')}
                    </Button>
                    <Button icon={<FilePdfOutlined />} loading={pdfBusy === 'download'} onClick={() => fetchPdf('download')}>
                      {t('downloadPdf')}
                    </Button>
                    {canSend && (
                      <Button type="primary" icon={<MailOutlined />} onClick={() => setEmailOpen(true)}>
                        {t('emailToCustomer')}
                      </Button>
                    )}
                    <Button icon={<DownloadOutlined />} onClick={() => exportStatementCsv(statement, t)}>
                      {t('exportCsv')}
                    </Button>
                    <Tooltip title={tc('refresh')}>
                      <Button icon={<ReloadOutlined />} onClick={() => statementQuery.refetch()} />
                    </Tooltip>
                  </Space>
                  <Tooltip
                    title={t('reconciliationCheckTooltip', {
                      gl: money(statement.reconciliation.gl_balance, base),
                      statement: money(statement.reconciliation.statement_balance, base),
                    })}
                  >
                    {statement.reconciliation.matched ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        {t('matchesReceivableLedger')}
                      </Tag>
                    ) : (
                      <Tag icon={<WarningOutlined />} color="warning">
                        {t('differsFromReceivableLedger', { amount: money(statement.reconciliation.difference, base) })}
                      </Tag>
                    )}
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Card>

          {integrityIssues.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message={t('settlementsUnmatched')}
              description={integrityIssues
                .map((s) => t('integrityDiffersDetail', { currency: s.currency, amount: money(s.integrity_difference, s.currency) }))
                .join(' · ')}
            />
          )}

          {statement.sections.length === 0 ? (
            <Card style={{ marginTop: 16 }}>
              <Empty description={t('noPostedCustomerDocuments')} />
            </Card>
          ) : (
            statement.sections.map(renderSection)
          )}

          <Card style={{ marginTop: 16 }}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
          </Card>

          {canSend && (
            <SendStatementModal
              open={emailOpen}
              statement={statement}
              mode={mode}
              currency={params?.currency}
              balanceLabel={money(statement.summary.closing_balance, base)}
              overdueLabel={money(statement.summary.overdue, base)}
              onPreview={() => fetchPdf('preview')}
              onClose={() => setEmailOpen(false)}
              onSent={() => {
                queryClient.invalidateQueries({ queryKey: ['customer-statement-dispatches', statement.customer.id] });
                setActiveTab('history');
              }}
            />
          )}
        </Spin>
      )}
      <style>{`.statement-row-overdue td { background: #FFF5F4; }`}</style>
    </div>
  );
};

export default CustomerStatementPage;
